import { create } from 'zustand'
import type { ConservativeLevel, SchoolName, YearPolicy } from '@/domain/types'
import type { DensityMethod } from '@/domain/density'

import p2023 from '@data/policy/2023.json'
import p2024 from '@data/policy/2024.json'
import p2025 from '@data/policy/2025.json'
import p2026 from '@data/policy/2026.json'
import preferenceV0 from '@data/preference/v0.json'
import narrativeV0 from '@data/narrative/v0.json'

const POLICIES: Record<number, YearPolicy> = {
  2023: p2023 as unknown as YearPolicy,
  2024: p2024 as unknown as YearPolicy,
  2025: p2025 as unknown as YearPolicy,
  2026: p2026 as unknown as YearPolicy
}

/** 当前默认对照年份：2026 未发布数据前回退到 2025 */
function defaultYear(): number {
  const y2026 = POLICIES[2026]
  if (y2026 && y2026.qualification_line !== null) return 2026
  return 2025
}

const STORAGE_KEY_UID = 'zhcz_uid'

function getOrCreateVisitorId(): string {
  try {
    const existing = localStorage.getItem(STORAGE_KEY_UID)
    if (existing && existing.length >= 16) return existing
    const uid = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2) + Date.now().toString(36))
    localStorage.setItem(STORAGE_KEY_UID, uid)
    return uid
  } catch {
    return 'anon-' + Math.random().toString(36).slice(2)
  }
}

/** UI 引导步骤（C 向导） */
export type WizardStep = 'idle' | 'score' | 'intent' | 'computing' | 'result'

interface AppState {
  visitorId: string
  year: number
  score: number | null
  intentSchool: SchoolName | null
  heat: Record<SchoolName, number>
  conservativeLevel: ConservativeLevel
  densityMethod: DensityMethod
  probabilityModel: 'ratio' | 'bayesian'
  wizardStep: WizardStep

  setYear: (y: number) => void
  setScore: (s: number | null) => void
  setIntent: (s: SchoolName | null) => void
  setHeat: (s: SchoolName, v: number) => void
  setConservativeLevel: (l: ConservativeLevel) => void
  setDensityMethod: (m: DensityMethod) => void
  setProbabilityModel: (m: 'ratio' | 'bayesian') => void
  setWizardStep: (s: WizardStep) => void
  resetHeat: () => void
}

const DEFAULT_HEAT: Record<SchoolName, number> = {
  邹城一中: 1.0,
  邹城二中: 1.0,
  实验中学: 1.0,
  兖矿一中: 1.0,
  孟子湖中学: 1.0
}

export const useApp = create<AppState>((set) => ({
  visitorId: getOrCreateVisitorId(),
  year: defaultYear(),
  score: null,
  intentSchool: null,
  heat: { ...DEFAULT_HEAT },
  conservativeLevel: 'normal',
  densityMethod: 'whittaker',
  probabilityModel: 'bayesian',
  wizardStep: 'idle',

  setYear: (y) => set({ year: y }),
  setScore: (s) => set({ score: s }),
  setIntent: (s) => set({ intentSchool: s }),
  setHeat: (s, v) => set((st) => ({ heat: { ...st.heat, [s]: v } })),
  setConservativeLevel: (l) => set({ conservativeLevel: l }),
  setDensityMethod: (m) => set({ densityMethod: m }),
  setProbabilityModel: (m) => set({ probabilityModel: m }),
  setWizardStep: (s) => set({ wizardStep: s }),
  resetHeat: () => set({ heat: { ...DEFAULT_HEAT } })
}))

export const POLICIES_MAP = POLICIES
export { POLICIES }
export const PREFERENCE = preferenceV0 as unknown as import('@/domain/types').PreferenceMatrix
export const NARRATIVE = narrativeV0 as unknown as import('@/domain/types').NarrativeLibrary
